import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Chip, CircularProgress, Typography, Tooltip, Slider, Switch } from '@material-ui/core';
import { GraphComponent } from './GraphComponent';
import { NodeList } from './NodeLists';
import { SERVER_NAME } from './config';

const safeJsonParse = (s) => {
  try { return JSON.parse(s); } catch { return null; }
};

const compactUsd = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '$0';
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
    minHeight: 0,
  },
  graphContainer: {
    position: 'relative',
    flex: '1 1 0%',
    minHeight: 0,
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: 'rgba(5,8,20,0.95)',
  },
  splitter: {
    height: 10,
    flex: '0 0 auto',
    cursor: 'row-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
    touchAction: 'none',
  },
  splitterBar: {
    height: 4,
    width: '60%',
    borderRadius: 999,
    background: 'rgba(158, 196, 255, 0.35)',
  },
  listContainer: {
    position: 'relative',
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: 'black',
    flex: '1 1 0%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  inlineEmpty: {
    padding: 12,
    fontSize: 12,
    color: '#9ea4d8',
    textAlign: 'center',
  },
}));

// Lightweight inline links pane for StoriesPage.
// Owns its own graphData and by default uses /api/tokenCounterparties.
// When rabbitMeta is provided (for RABBIT_% stories), graphData and tokenList
// are built directly from the story's meta.entities / meta.evidence instead
// of querying live counterparties.
//
// When onSnapshot is provided, the latest (token, graphData, tokenList) are
// reported upward so the full /links view can be pre-hydrated with exactly
// the same forensics graph.
export default function StoriesLinksPane({ token, tokens, seedCacheKey: seedCacheKeyProp, seedKind = 'token', collapseMultiSeed = false, primaryTokenMeta, deepDives = null, showGraph = true, showList = true, isMobile = false, onPromotePrimary, onUpdatingChange, rabbitMeta, onSnapshot, sideBySide = false, forceShowList = false, graphFlex = null, listFlex = null }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [resolvingNames, setResolvingNames] = useState(false);
  const lastBusyRef = useRef(null);
  const [showSlowSpinner, setShowSlowSpinner] = useState(false);
  const [loadProgress, setLoadProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [tokenList, setTokenList] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [nodeContextMenu, setNodeContextMenu] = useState(null);
  const rootRef = useRef();
  const graphRef = useRef();
  const graphContainerRef = useRef();
  const [showGraphToolbar, setShowGraphToolbar] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [labelSize, setLabelSize] = useState(3);
  const [emptyGraceExpired, setEmptyGraceExpired] = useState(false);
  const lastSplitClientYRef = useRef(null);
  const [graphWidth, setGraphWidth] = useState(0);
  const [graphHeight, setGraphHeight] = useState(0);
  const graphSizeRef = useRef({ width: 0, height: 0 });

  const graphSignature = useCallback((g) => {
    const nodes = g && Array.isArray(g.nodes) ? g.nodes : [];
    const links = g && Array.isArray(g.links) ? g.links : [];
    const nodeKey = nodes.slice(0, 20).map((n) => String(n && n.id ? n.id : '')).join('|');
    const linkKey = links.slice(0, 20).map((l) => {
      const s = typeof l.source === 'string' ? l.source : (l.source && l.source.id);
      const t = typeof l.target === 'string' ? l.target : (l.target && l.target.id);
      return `${s || ''}>${t || ''}`;
    }).join('|');
    return `${nodes.length}:${links.length}:${nodeKey}:${linkKey}`;
  }, []);

  const tokenListSignature = useCallback((list) => {
    const rows = Array.isArray(list) ? list : [];
    return `${rows.length}:${rows.slice(0, 20).map((row) => String((row && (row.id || row.address)) || '')).join('|')}`;
  }, []);

  const setGraphDataIfChanged = useCallback((next) => {
    setGraphData((prev) => (graphSignature(prev) === graphSignature(next) ? prev : next));
  }, [graphSignature]);

  const setTokenListIfChanged = useCallback((next) => {
    setTokenList((prev) => (tokenListSignature(prev) === tokenListSignature(next) ? prev : next));
  }, [tokenListSignature]);

  const pumpSignals = useMemo(() => {
    const metas = Array.isArray(rabbitMeta) ? rabbitMeta : (rabbitMeta ? [rabbitMeta] : []);
    if (!metas.length) return [];

    const out = [];
    const seen = new Set();
    const push = (label, color, background) => {
      const key = String(label || '').toLowerCase();
      if (!label || seen.has(key)) return;
      seen.add(key);
      out.push({ label, color, background });
    };

    metas.forEach((meta) => {
      if (!meta || typeof meta !== 'object') return;
      const severity = String(meta?.pump?.severity || '').toLowerCase();
      if (severity === 'extreme') push('Extreme pump', '#ff7a7a', 'rgba(255,122,122,0.12)');
      else if (severity === 'high') push('High pump', '#ffe082', 'rgba(255,224,130,0.12)');
      else if (severity === 'medium') push('Medium pump', '#9ef7c5', 'rgba(158,247,197,0.10)');

      const participation = String(meta?.participation_type || meta?.confirmation_quality || '').trim();
      if (participation) push(participation, '#9ea4d8', 'rgba(255,255,255,0.06)');

      const liquidityHint = String(meta?.pump?.liquidity_hint || '').trim();
      if (liquidityHint) push(liquidityHint, '#9ea4d8', 'rgba(255,255,255,0.06)');

      const tvl = Number(meta?.pump?.current_pool_tvl_usd || meta?.pool?.tvl_usd || meta?.pool?.tvlUSD || 0);
      if (tvl > 0) push(`TVL ${compactUsd(tvl)}`, '#cfd3ff', 'rgba(77,121,255,0.10)');
    });

    return out.slice(0, 5);
  }, [rabbitMeta]);

  const measureGraphContainer = useCallback(() => {
    const el = graphContainerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const w = rect.width || 0;
    const h = rect.height || 0;
    const prev = graphSizeRef.current || { width: 0, height: 0 };
    const nextW = w ? Math.round(w) : 0;
    const nextH = h ? Math.round(h) : 0;
    if (nextW && nextW !== prev.width) {
      graphSizeRef.current = { ...graphSizeRef.current, width: nextW };
      setGraphWidth(nextW);
    }
    if (nextH && nextH !== prev.height) {
      graphSizeRef.current = { ...graphSizeRef.current, height: nextH };
      setGraphHeight(nextH);
    }
  }, []);

  const readStoredSplit = () => {
    try {
      if (typeof window === 'undefined') return null;
      const raw = window.localStorage.getItem('storiesEvidenceSplit');
      const v = Number(raw);
      if (!Number.isFinite(v)) return null;
      if (v <= 0 || v >= 1) return null;
      return v;
    } catch (e) {
      return null;
    }
  };

  const [splitRatio, setSplitRatio] = useState(() => readStoredSplit() ?? 0.55);

  const writeStoredSplit = (next) => {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.setItem('storiesEvidenceSplit', String(next));
    } catch (e) {
    }
  };

  const applySplitFromPointer = useCallback((clientY) => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const totalH = rect.height || 0;
    if (!totalH) return;

    const y = clientY - rect.top;
    const next = Math.max(0.2, Math.min(0.8, y / totalH));
    setSplitRatio(next);
    writeStoredSplit(next);
  }, []);

  const startSplitDrag = useCallback((e) => {
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
    } catch (err) {
    }

    const move = (ev) => {
      const y = ev && ev.clientY != null ? ev.clientY : null;
      if (y == null) return;
      lastSplitClientYRef.current = y;
      applySplitFromPointer(y);
    };

    const up = () => {
      const y = lastSplitClientYRef.current;
      if (y != null) {
        applySplitFromPointer(y);
      }
      // Re-measure after flex reflow and refresh the canvas.
      try {
        requestAnimationFrame(() => {
          try {
            measureGraphContainer();
            if (graphRef && graphRef.current && typeof graphRef.current.refresh === 'function') {
              graphRef.current.refresh();
            }
          } catch (err) {
          }
        });
      } catch (err) {
      }
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);

    const initialY = e && e.clientY != null ? e.clientY : null;
    if (initialY != null) {
      lastSplitClientYRef.current = initialY;
      applySplitFromPointer(initialY);
    }
  }, [applySplitFromPointer]);

  // Simple in-memory cache per token for this session
  const cacheRef = useRef(new Map());

  const addressMetaCacheRef = useRef(new Map());

  const normalizeAddress = (addr) => {
    if (!addr) return '';
    const s = String(addr).trim().toLowerCase();
    if (!s) return '';
    const base = s.includes('.') ? s.split('.')[0] : s;
    if (!base.startsWith('0x')) return '';
    if (!/^0x[0-9a-f]+$/.test(base)) return '';
    return base;
  };

  const seedTokens = useMemo(() => {
    const arr = Array.isArray(tokens) ? tokens : (token ? [token] : []);
    const out = [];
    const seen = new Set();
    (arr || []).forEach((t) => {
      const a = normalizeAddress(t);
      if (!a) return;
      if (seen.has(a)) return;
      seen.add(a);
      out.push(a);
    });
    return out;
  }, [token, tokens]);

  const seedCacheKey = useMemo(() => {
    if (seedCacheKeyProp) return String(seedCacheKeyProp);
    if (!seedTokens || !seedTokens.length) return '';
    return seedTokens.join(',');
  }, [seedCacheKeyProp, seedTokens]);

  const activeSeedTokens = useMemo(() => {
    if (!seedCacheKey) return [];
    const raw = String(seedCacheKey).startsWith('hotlinks:')
      ? String(seedCacheKey).split(':').pop()
      : String(seedCacheKey);
    return raw.split(',').map((x) => normalizeAddress(x)).filter(Boolean);
  }, [seedCacheKey]);

  const wantsCollapsed = !!collapseMultiSeed && seedTokens.length > 1;

  useEffect(() => {
    if (wantsCollapsed) {
      setExpanded(false);
    } else {
      setExpanded(true);
    }
  }, [seedCacheKey, wantsCollapsed]);

  const looksLikeAddress = (s) => {
    if (!s) return false;
    const v = String(s);
    return v.startsWith('0x') && v.length >= 10;
  };

  const mapWithConcurrency = async (items, concurrency, fn) => {
    const arr = Array.isArray(items) ? items : [];
    const limit = Math.max(1, Number(concurrency || 1));
    const out = new Array(arr.length);
    let idx = 0;

    const worker = async () => {
      while (true) {
        const i = idx;
        idx += 1;
        if (i >= arr.length) return;
        out[i] = await fn(arr[i], i);
      }
    };

    const workers = [];
    for (let i = 0; i < Math.min(limit, arr.length); i += 1) workers.push(worker());
    await Promise.all(workers);
    return out;
  };

  const mergeDeepDiveIntoGraph = useCallback((baseGraph, baseTokenList, deepDiveStories, primaryId) => {
    if (!baseGraph || !Array.isArray(baseGraph.nodes) || !Array.isArray(baseGraph.links)) {
      return { graphData: baseGraph, tokenList: baseTokenList };
    }

    const deep = Array.isArray(deepDiveStories) ? deepDiveStories : [];
    if (!deep.length) return { graphData: baseGraph, tokenList: baseTokenList };

    const nodes = [...(baseGraph.nodes || [])];
    const links = [...(baseGraph.links || [])];
    const nodeById = new Map();
    nodes.forEach((n) => {
      if (!n || !n.id) return;
      nodeById.set(String(n.id).toLowerCase(), n);
    });

    const linkKeySet = new Set();
    links.forEach((l) => {
      const rawS = typeof l.source === 'string' ? l.source : l.source?.id;
      const rawT = typeof l.target === 'string' ? l.target : l.target?.id;
      const s = rawS ? String(rawS).toLowerCase() : '';
      const t = rawT ? String(rawT).toLowerCase() : '';
      if (!s || !t) return;
      linkKeySet.add(`${s}>>${t}`);
    });

    const ensureNode = (id, label, icon, kind) => {
      if (!id) return;
      const key = String(id).toLowerCase();
      if (nodeById.has(key)) return;
      nodes.push({
        id,
        label: label || formatShortAddress(id),
        icon: icon || null,
        chain: (primaryTokenMeta && primaryTokenMeta.chain) ? String(primaryTokenMeta.chain).toUpperCase() : 'ETH',
        color: colorHexToNumber(getColorForAddress(id)),
        metaType: kind || 'deep_dive',
      });
      nodeById.set(key, nodes[nodes.length - 1]);
    };

    const deepDiveColorFor = (questionType) => {
      const qt = questionType ? String(questionType).toUpperCase() : '';
      if (qt === 'TOP_COUNTERPARTY') return '#b45aff';
      if (qt === 'COUNTERPARTY_WALK') return '#00ffa0';
      if (qt === 'BRIDGE_PATH' || qt === 'BRIDGE' || qt === 'L2_BRIDGE_PATH') return '#ffb020';
      return '#ff00ff';
    };

    const deepDiveLinked = new Set();

    const normalizeDeepDiveType = (questionType) => {
      const qt = questionType ? String(questionType).trim().toUpperCase() : '';
      if (!qt) return '';
      if (qt === 'TOP_COUNTERPARTY' || qt === 'TOP-COUNTERPARTY') return 'TOP_COUNTERPARTY';
      if (qt === 'COUNTERPARTY_WALK' || qt === 'COUNTERPARTY-WALK') return 'COUNTERPARTY_WALK';
      if (qt === 'BRIDGE_PATH' || qt === 'BRIDGE-PATH' || qt === 'BRIDGE' || qt === 'L2_BRIDGE_PATH' || qt === 'L2-BRIDGE-PATH') return 'BRIDGE_PATH';
      if (qt === 'TOP_COUNTERPARTY'.toLowerCase().toUpperCase() && qt.includes('TOP') && qt.includes('COUNTERPARTY')) return 'TOP_COUNTERPARTY';
      if (qt.includes('TOP_COUNTERPARTY') || qt.includes('TOP COUNTERPARTY') || qt === 'TOP_COUNTERPARTY'.toUpperCase()) return 'TOP_COUNTERPARTY';
      if (qt.includes('TOP_COUNTERPARTY') || qt.includes('TOP COUNTERPARTY') || qt === 'TOP_COUNTERPARTY') return 'TOP_COUNTERPARTY';
      if (qt.includes('TOP_COUNTERPARTY') || qt.includes('TOP COUNTERPARTY')) return 'TOP_COUNTERPARTY';
      if (qt === 'TOP_COUNTERPARTY') return 'TOP_COUNTERPARTY';
      if (qt === 'COUNTERPARTY_WALK') return 'COUNTERPARTY_WALK';
      if (qt === 'TOP_COUNTERPARTY'.toUpperCase()) return 'TOP_COUNTERPARTY';
      if (qt === 'COUNTERPARTY_WALK'.toUpperCase()) return 'COUNTERPARTY_WALK';
      if (qt === 'TOP_COUNTERPARTY'.replace(/_/g, '')) return 'TOP_COUNTERPARTY';
      if (qt === 'COUNTERPARTY_WALK'.replace(/_/g, '')) return 'COUNTERPARTY_WALK';
      if (qt === 'TOP_COUNTERPARTY'.toUpperCase()) return 'TOP_COUNTERPARTY';
      if (qt === 'COUNTERPARTY_WALK'.toUpperCase()) return 'COUNTERPARTY_WALK';
      if (qt === 'TOP_COUNTERPARTY'.toUpperCase()) return 'TOP_COUNTERPARTY';
      if (qt === 'COUNTERPARTY_WALK'.toUpperCase()) return 'COUNTERPARTY_WALK';
      if (qt === 'TOP_COUNTERPARTY'.toUpperCase()) return 'TOP_COUNTERPARTY';
      if (qt === 'COUNTERPARTY_WALK'.toUpperCase()) return 'COUNTERPARTY_WALK';
      if (qt === 'TOP_COUNTERPARTY'.toUpperCase()) return 'TOP_COUNTERPARTY';
      if (qt === 'COUNTERPARTY_WALK'.toUpperCase()) return 'COUNTERPARTY_WALK';

      // canonicalize common lowercase DB values
      if (qt === 'TOP_COUNTERPARTY'.toLowerCase().toUpperCase() && String(questionType).toLowerCase() === 'top_counterparty') return 'TOP_COUNTERPARTY';
      if (qt === 'COUNTERPARTY_WALK'.toLowerCase().toUpperCase() && String(questionType).toLowerCase() === 'counterparty_walk') return 'COUNTERPARTY_WALK';
      if (String(questionType).toLowerCase() === 'top_counterparty') return 'TOP_COUNTERPARTY';
      if (String(questionType).toLowerCase() === 'counterparty_walk') return 'COUNTERPARTY_WALK';

      return qt;
    };

    const addLink = (source, target, id, value, questionType) => {
      const s = source ? String(source).toLowerCase() : '';
      const t = target ? String(target).toLowerCase() : '';
      if (!s || !t) return;
      // Allow a deep-dive link to coexist with an existing base link between
      // the same endpoints so the trail color is visible.
      const kind = 'deep_dive';
      const ddType = normalizeDeepDiveType(questionType);
      const key = `${s}>>${t}>>${kind}>>${ddType || (questionType ? String(questionType) : '')}`;
      if (linkKeySet.has(key)) return;
      linkKeySet.add(key);
      links.push({
        id,
        source,
        target,
        value,
        metaType: 'deep_dive',
        ddType: ddType || (questionType ? String(questionType) : ''),
        color: deepDiveColorFor(ddType || questionType),
      });

      deepDiveLinked.add(s);
      deepDiveLinked.add(t);
    };

    const primary = primaryId || token;
    deep.forEach((d, idx) => {
      if (!d) return;
      const qType = d && (d.question_type || (d.meta && d.meta.question_type) || (d.meta && d.meta.questionType))
        ? String(d.question_type || (d.meta && (d.meta.question_type || d.meta.questionType)) || '')
        : '';
      const qTypeNorm = normalizeDeepDiveType(qType);
      const rawMeta = d.meta && typeof d.meta === 'object'
        ? d.meta
        : (typeof d.meta_json === 'string' ? safeJsonParse(d.meta_json) : null);
      const result = rawMeta && rawMeta.result ? rawMeta.result : null;

      // Prefer explicit deep-walk trail format when present.
      const pathNodes = result && Array.isArray(result.path_nodes) ? result.path_nodes : null;
      const pathEdges = result && Array.isArray(result.path_edges) ? result.path_edges : null;
      if (pathEdges && pathEdges.length) {
        // Add all nodes first so edges can reference them.
        (pathNodes || []).forEach((pn) => {
          const addr = pn && (pn.address || pn.id) ? String(pn.address || pn.id) : '';
          if (!addr) return;
          ensureNode(addr, pn.label || pn.name || pn.symbol || null, pn.icon || null, pn.role || 'deep_dive_path');
        });

        pathEdges.forEach((pe, eidx) => {
          if (!pe) return;
          const src = pe.source || pe.src || pe.from;
          const dst = pe.target || pe.dst || pe.to;
          if (!src || !dst) return;
          ensureNode(String(src), null, null, 'deep_dive_path');
          ensureNode(String(dst), null, null, 'deep_dive_path');

          const txc = Number(pe.tx_count || pe.txCount || 0);
          const v = Math.max(1.4, Math.min(5.0, 1.4 + 0.7 * Math.log1p(Math.max(0, txc))));
          addLink(String(src), String(dst), `deep-path-${d.id || idx}-${eidx}`, v, qTypeNorm || qType || 'COUNTERPARTY_WALK');
        });
        return;
      }

      const cpObj = result && result.counterparty ? result.counterparty : null;
      const cp = (() => {
        if (!result) return '';
        if (cpObj && typeof cpObj === 'string') return String(cpObj);
        if (cpObj && typeof cpObj === 'object' && cpObj.address) return String(cpObj.address);
        if (result.counterparty_address) return String(result.counterparty_address);
        if (result.counterpartyAddress) return String(result.counterpartyAddress);
        if (result.counterparty && typeof result.counterparty === 'object' && result.counterparty.addr) return String(result.counterparty.addr);
        if (result.counterparty && typeof result.counterparty === 'object' && result.counterparty.id) return String(result.counterparty.id);
        return '';
      })();
      if (!cp) return;

      const cpLabel = (cpObj && typeof cpObj === 'object')
        ? (cpObj.name || cpObj.label || cpObj.symbol || null)
        : (result && (result.counterparty_name || result.counterpartyName || result.counterparty_label || result.counterpartyLabel) ?
          String(result.counterparty_name || result.counterpartyName || result.counterparty_label || result.counterpartyLabel) : null);
      const cpIcon = (cpObj && typeof cpObj === 'object' && cpObj.icon) ? cpObj.icon : (result && (result.counterparty_icon || result.counterpartyIcon) ? String(result.counterparty_icon || result.counterpartyIcon) : null);

      ensureNode(cp, cpLabel, cpIcon, 'deep_dive_counterparty');

      // Make deep-dive edges visually stronger than generic counterparties.
      const v = Math.max(1.2, Math.min(3.5, 1.2 + 0.6 * Math.log1p(Number(result && result.tx_count ? result.tx_count : 0))));
      addLink(primary, cp, `deep-${d.id || idx}`, v, qTypeNorm || qType || 'TOP_COUNTERPARTY');
    });

    // Token list: include deep-dive counterparties so they appear in the list pane.
    const listOut = Array.isArray(baseTokenList) ? [...baseTokenList] : [];
    const listSeen = new Set(listOut.map((r) => String((r && (r.id || r.address)) || '').toLowerCase()).filter(Boolean));
    nodes.forEach((n) => {
      const rawId = n && n.id ? String(n.id) : '';
      const idLower = rawId ? rawId.toLowerCase() : '';
      if (!idLower || listSeen.has(idLower)) return;

      // Avoid listing dangling/unconnected deep-dive nodes.
      const isDeepDiveNode = (n && n.metaType && String(n.metaType).startsWith('deep_dive'));
      if (isDeepDiveNode && !deepDiveLinked.has(idLower)) return;

      listSeen.add(idLower);
      listOut.push({
        id: rawId,
        address: rawId,
        label: n.label,
        icon: n.icon,
        color: n.icon ? null : getColorForAddress(rawId),
        chain: (n.chain || '').toUpperCase(),
        type: n.metaType || n.kind || '',
        count: 0,
        symbol: '',
        likes: 0,
      });
    });

    return { graphData: { nodes, links }, tokenList: listOut };
  }, [token, primaryTokenMeta]);

  const resolveAddresses = async (addresses) => {
    const cleaned = Array.from(
      new Set(
        (addresses || [])
          .map((a) => normalizeAddress(a))
          .filter((a) => a && looksLikeAddress(a))
      )
    );

    if (!cleaned.length) return;

    const missing = cleaned.filter((a) => !addressMetaCacheRef.current.has(a));
    if (!missing.length) return;

    const chunks = [];
    const MAX = 500;
    for (let i = 0; i < missing.length; i += MAX) {
      chunks.push(missing.slice(i, i + MAX));
    }

    for (const chunk of chunks) {
      try {
        const resp = await axios.post(`${SERVER_NAME}/addressMetaBatch`, { addresses: chunk });
        const payload = resp ? resp.data : null;
        const rows = Array.isArray(payload)
          ? payload
          : (payload && Array.isArray(payload.data)
            ? payload.data
            : (payload && payload.data && Array.isArray(payload.data.data)
              ? payload.data.data
              : []));
        rows.forEach((row, idx) => {
          const reqAddr = chunk[idx];
          const addr = normalizeAddress((row && (row.address || row.addr)) || reqAddr);
          if (!addr) return;
          addressMetaCacheRef.current.set(addr, row || null);
        });
      } catch (e) {
        // Important: do NOT mark these addresses as "known null" on request
        // failure. Otherwise a transient server/network issue permanently
        // prevents name/symbol lookups for the rest of the session.
      }
    }
  };

  const applyResolvedLabels = (maybeGraph) => {
    const g = maybeGraph || graphData;
    if (!g || !Array.isArray(g.nodes) || !g.nodes.length) return { changed: false, next: g };

    let changed = false;
    g.nodes.forEach((n) => {
      if (!n || !n.id) return;
      const addr = normalizeAddress(n.id);
      if (!addr) return;
      const meta = addressMetaCacheRef.current.get(addr);
      if (!meta) return;
      const nextLabel = meta.name || meta['n.name'] || meta.symbol || meta['n.symbol'] || n.label;
      const nextIcon = meta.icon || meta['n.icon'] || meta.icon2 || meta['n.icon2'] || n.icon || null;
      if (nextLabel && nextLabel !== n.label) {
        n.label = nextLabel;
        changed = true;
      }
      if (nextIcon && nextIcon !== n.icon) {
        n.icon = nextIcon;
        changed = true;
      }
    });

    return {
      changed,
      next: g,
    };
  };

  const mergeGraphs = (graphs) => {
    const out = { nodes: [], links: [] };
    const nodeById = new Map();
    const seenLinks = new Set();

    (graphs || []).forEach((g) => {
      if (!g) return;
      (g.nodes || []).forEach((n) => {
        if (!n || !n.id) return;
        const id = String(n.id).toLowerCase();
        if (nodeById.has(id)) return;
        nodeById.set(id, n);
        out.nodes.push(n);
      });

      (g.links || []).forEach((l) => {
        if (!l) return;
        const rawS = typeof l.source === 'string' ? l.source : l.source?.id;
        const rawT = typeof l.target === 'string' ? l.target : l.target?.id;
        const s = rawS ? String(rawS).toLowerCase() : '';
        const t = rawT ? String(rawT).toLowerCase() : '';
        if (!s || !t) return;
        const key = `${s}>>${t}`;
        if (seenLinks.has(key)) return;
        seenLinks.add(key);
        out.links.push(l);
      });
    });

    return out;
  };

  const mergeTokenLists = (lists) => {
    const out = [];
    const seen = new Set();
    (lists || []).forEach((list) => {
      (list || []).forEach((row) => {
        if (!row) return;
        const id = row.id || row.address;
        if (!id) return;
        const key = String(id).toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        out.push(row);
      });
    });
    return out;
  };

  useEffect(() => {
    const busy = !!loading || !!resolvingNames;
    if (!busy) {
      if (showSlowSpinner) setShowSlowSpinner(false);
      return;
    }

    const t = setTimeout(() => {
      setShowSlowSpinner(true);
    }, 2000);

    return () => {
      clearTimeout(t);
    };
  }, [loading, resolvingNames, showSlowSpinner]);

  useEffect(() => {
    if (!onUpdatingChange) return;
    const busy = !!loading || !!resolvingNames;
    if (lastBusyRef.current === busy) return;
    lastBusyRef.current = busy;
    onUpdatingChange(busy);
  }, [loading, resolvingNames, onUpdatingChange]);

  // Keep the inline graph sized to its container.
  useEffect(() => {
    const el = graphContainerRef.current;
    if (!el) return;

    measureGraphContainer();

    const observer = new ResizeObserver(() => {
      measureGraphContainer();
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [measureGraphContainer]);

  // Some browsers/layouts may not emit ResizeObserver events for flex changes
  // during drag; explicitly re-measure when the split ratio changes.
  useEffect(() => {
    if (!showGraph) return;
    try {
      requestAnimationFrame(() => {
        try {
          measureGraphContainer();
          if (graphRef && graphRef.current && typeof graphRef.current.refresh === 'function') {
            graphRef.current.refresh();
          }
        } catch (err) {
        }
      });
    } catch (err) {
    }
  }, [splitRatio, showGraph, measureGraphContainer]);

  useEffect(() => {
    const onToggleToolbar = () => {
      setShowGraphToolbar((prev) => !prev);
    };
    window.addEventListener('toggle-stories-graph-toolbar', onToggleToolbar);
    return () => {
      window.removeEventListener('toggle-stories-graph-toolbar', onToggleToolbar);
    };
  }, []);

  useEffect(() => {
    if (loading) {
      if (emptyGraceExpired) setEmptyGraceExpired(false);
      return undefined;
    }

    if ((graphData && Array.isArray(graphData.nodes) && graphData.nodes.length) || (tokenList && tokenList.length)) {
      if (emptyGraceExpired) setEmptyGraceExpired(false);
      return undefined;
    }

    const t = setTimeout(() => {
      setEmptyGraceExpired(true);
    }, 4000);

    return () => {
      clearTimeout(t);
    };
  }, [loading, graphData, tokenList, emptyGraceExpired]);

  useEffect(() => {
    if (!activeSeedTokens.length) {
      // No token means nothing to load; ensure parent knows we're idle.
      if (loading) setLoading(false);
      if (resolvingNames) setResolvingNames(false);
      if (showSlowSpinner) setShowSlowSpinner(false);
      return;
    }

    if (wantsCollapsed && !expanded) {
      const nodes = activeSeedTokens.map((id) => ({
        id,
        label: formatShortAddress(id),
        icon: null,
        chain: 'ETH',
        color: colorHexToNumber(getColorForAddress(id)),
        expandScale: 1.2,
      }));
      const rows = activeSeedTokens.map((id) => ({ address: id, label: formatShortAddress(id), chain: 'ETH', icon: null, type: 'wallet' }));
      const nextGraph = { nodes, links: [] };
      const nextTokenList = createTokenList(rows, true, activeSeedTokens[0], primaryTokenMeta);

      if (loading) setLoading(false);
      if (resolvingNames) setResolvingNames(false);
      if (showSlowSpinner) setShowSlowSpinner(false);
      setGraphDataIfChanged(nextGraph);
      setTokenListIfChanged(nextTokenList);
      setError('');
      return;
    }

    // If story meta is provided, synthesize graph/list directly from the
    // story meta instead of querying /api/tokenCounterparties.
    if (rabbitMeta && typeof rabbitMeta === 'object') {
      // Group mode: rabbitMeta is passed as an array of { token, meta }.
      if (Array.isArray(rabbitMeta)) {
        const builtGraphs = [];
        const builtLists = [];
        rabbitMeta.forEach((row) => {
          if (!row || typeof row !== 'object') return;
          const rowToken = row.token;
          const meta = row.meta;
          if (!rowToken || !meta || typeof meta !== 'object') return;
          let built = null;
          if (meta.type === 'FLOW') {
            built = buildGraphFromFlowMeta(rowToken, meta, null);
          } else if (meta.entities) {
            built = buildGraphFromRabbitMeta(rowToken, meta, null);
          } else if (Array.isArray(meta.nodes) || Array.isArray(meta.edges)) {
            built = buildGraphFromFlowMeta(rowToken, meta, null);
          }
          if (!built) return;
          if (built.graphData) builtGraphs.push(built.graphData);
          if (built.tokenList) builtLists.push(built.tokenList);
        });

        if (builtGraphs.length) {
          const merged = mergeGraphs(builtGraphs);
          const mergedList = mergeTokenLists(builtLists);
          const mergedOut = mergeDeepDiveIntoGraph(merged, mergedList, deepDives, activeSeedTokens[0] || token);
          if (loading) setLoading(false);
          if (resolvingNames) setResolvingNames(false);
          if (showSlowSpinner) setShowSlowSpinner(false);
          setGraphDataIfChanged(mergedOut.graphData);
          setTokenListIfChanged(mergedOut.tokenList);
          if (onSnapshot) {
            onSnapshot({
              token,
              graphData: mergedOut.graphData,
              tokenList: mergedOut.tokenList,
              source: 'RABBIT',
            });
          }
          setError('');
          return;
        }
      } else {
        let built = null;
        if (rabbitMeta.type === 'FLOW') {
          built = buildGraphFromFlowMeta(token, rabbitMeta, primaryTokenMeta);
        } else if (rabbitMeta.entities) {
          built = buildGraphFromRabbitMeta(token, rabbitMeta, primaryTokenMeta);
        } else if (Array.isArray(rabbitMeta.nodes) || Array.isArray(rabbitMeta.edges)) {
          // Generic node/edge style meta: render with FLOW-style builder.
          built = buildGraphFromFlowMeta(token, rabbitMeta, primaryTokenMeta);
        }

        if (built) {
          const { graphData: rabbitGraph, tokenList: rabbitTokenList } = built;
          const mergedOut = mergeDeepDiveIntoGraph(rabbitGraph, rabbitTokenList, deepDives, token);
          if (loading) setLoading(false);
          if (resolvingNames) setResolvingNames(false);
          if (showSlowSpinner) setShowSlowSpinner(false);
          setGraphDataIfChanged(mergedOut.graphData);
          setTokenListIfChanged(mergedOut.tokenList);
          if (onSnapshot) {
            onSnapshot({
              token,
              graphData: mergedOut.graphData,
              tokenList: mergedOut.tokenList,
              source: 'RABBIT',
            });
          }
          setError('');
          return;
        }
      }
    }

    const cacheKey = seedCacheKey || token;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      // Even when serving from cache, briefly signal an update so the
      // parent can show the "Updating forensics…" status while the
      // inline graph/list swap to the new primary token.
      const cachedGraph = cached.graphData || { nodes: [], links: [] };
      const cachedList = cached.tokenList || [];
      const mergedOut = mergeDeepDiveIntoGraph(cachedGraph, cachedList, deepDives, activeSeedTokens[0] || token);
      if (loading) setLoading(false);
      if (resolvingNames) setResolvingNames(false);
      if (showSlowSpinner) setShowSlowSpinner(false);
      setGraphDataIfChanged(mergedOut.graphData);
      setTokenListIfChanged(mergedOut.tokenList);
      if (onSnapshot) {
        onSnapshot({
          token: activeSeedTokens[0] || token,
          graphData: mergedOut.graphData,
          tokenList: mergedOut.tokenList,
          source: rabbitMeta ? 'RABBIT' : 'ETHERSCAN',
        });
      }
      setError('');
      return;
    }

    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        setLoadProgress({ done: 0, total: activeSeedTokens.length });

        const nowSec = Math.floor(Date.now() / 1000);
        // For very large multi-seed loads (e.g. NEW_WALLETS sample of 40),
        // keep the query window tight to avoid overloading ClickHouse.
        const windowSec = activeSeedTokens.length > 12 ? (10 * 60) : (24 * 60 * 60);
        const thenAgoSec = nowSec - windowSec;

        const rawBySeed = new Map();

        const endpoint = seedKind === 'address' ? '/addressCounterparties' : '/tokenCounterparties';
        const seedParam = seedKind === 'address' ? 'address' : 'token';

        const MAX_CONCURRENCY = activeSeedTokens.length > 25 ? 2 : (activeSeedTokens.length > 5 ? 4 : 3);
        await mapWithConcurrency(activeSeedTokens, MAX_CONCURRENCY, async (seed) => {
          try {
            const resp = await axios.get(`${SERVER_NAME}${endpoint}`, {
              params: {
                [seedParam]: seed,
                startTime: thenAgoSec,
                endTime: nowSec,
                limit: activeSeedTokens.length > 12 ? 80 : 200,
              },
            });

            if (cancelled) return null;

            const raw = resp.data || [];
            rawBySeed.set(seed, raw);
          } catch (err) {
            // Non-fatal: if a single seed fails to fetch, treat it as having
            // no counterparties and keep going so we can still build a merged
            // graph for the remaining seeds.
            if (!cancelled) {
              rawBySeed.set(seed, []);
            }
          } finally {
            if (!cancelled) {
              setLoadProgress((prev) => {
                const total = prev && Number.isFinite(Number(prev.total)) ? Number(prev.total) : activeSeedTokens.length;
                const nextDone = Math.min(total, (prev && Number.isFinite(Number(prev.done)) ? Number(prev.done) : 0) + 1);
                return { done: nextDone, total };
              });
            }
          }
          return null;
        });

        if (cancelled) return;

        const mergedRows = [];
        activeSeedTokens.forEach((seed) => {
          const rows = rawBySeed.get(seed) || [];
          mergedRows.push(...rows);
        });

        const sorted = [...mergedRows].sort((a, b) => {
          const aHasIcon = !!a.icon;
          const bHasIcon = !!b.icon;
          if (aHasIcon !== bHasIcon) return bHasIcon - aHasIcon;
          return 0;
        });

        const rawBySeedForGraph = new Map();
        activeSeedTokens.forEach((seed) => {
          const rows = rawBySeed.get(seed) || [];
          rawBySeedForGraph.set(seed, rows.slice(0, 80));
        });

        const graphBuilt = activeSeedTokens.length > 1
          ? buildGraphFromMultiCounterparties(activeSeedTokens, rawBySeedForGraph, primaryTokenMeta)
          : buildGraphFromCounterparties(activeSeedTokens[0], rawBySeedForGraph.get(activeSeedTokens[0]) || [], primaryTokenMeta);

        const baseGraph = { nodes: graphBuilt.nodes, links: graphBuilt.links };
        const baseList = createTokenList(sorted, true, activeSeedTokens[0], primaryTokenMeta);
        const mergedOut = mergeDeepDiveIntoGraph(baseGraph, baseList, deepDives, activeSeedTokens[0]);
        const nextGraph = mergedOut.graphData;
        const nextTokenList = mergedOut.tokenList;

        cacheRef.current.set(cacheKey, { graphData: nextGraph, tokenList: nextTokenList });
        setGraphDataIfChanged(nextGraph);
        setTokenListIfChanged(nextTokenList);
        if (onSnapshot) {
          onSnapshot({
            token: activeSeedTokens[0],
            graphData: nextGraph,
            tokenList: nextTokenList,
            source: 'ETHERSCAN',
          });
        }
      } catch (err) {
        if (!cancelled) {
          console.error('StoriesLinksPane: error fetching counterparties', err);
          setError('Unable to load evidence preview.');
          setGraphDataIfChanged({ nodes: [], links: [] });
          setTokenListIfChanged([]);
        }
      } finally {
        // Always clear the loading flag for this pane when a request ends.
        // If a newer request has started, it will set loading=true again.
        if (!cancelled) {
          setLoading(false);
          setLoadProgress({ done: 0, total: 0 });
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [seedCacheKey, activeSeedTokens, seedKind, primaryTokenMeta, rabbitMeta, wantsCollapsed, expanded, deepDives, mergeDeepDiveIntoGraph, setGraphDataIfChanged, setTokenListIfChanged, token, onSnapshot]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!graphData || !Array.isArray(graphData.nodes) || !graphData.nodes.length) return;

        setResolvingNames(true);

        // Reduce perceived latency: only resolve a capped set of addresses
        // that are still unlabeled (label looks like an address). The cache
        // persists for the session so subsequent stories will be faster.
        const ids = [];
        const MAX_PER_STORY = 180;
        for (const n of graphData.nodes) {
          if (!n || !n.id) continue;
          const idStr = String(n.id);
          const addr = normalizeAddress(idStr);
          if (!addr) continue;
          if (addressMetaCacheRef.current.has(addr)) continue;
          const label = n.label != null ? String(n.label) : '';
          const labelLooksAddress = label.startsWith('0x') || label.includes('...');
          if (!labelLooksAddress) continue;
          ids.push(addr);
          if (ids.length >= MAX_PER_STORY) break;
        }

        await resolveAddresses(ids);
        if (cancelled) return;

        if (!cancelled) {
          const { changed } = applyResolvedLabels(graphData);
          // ForceGraph can re-heat/re-layout when graphData reference changes.
          // Avoid setGraphData here; instead refresh the renderer so labels/icons
          // update without perturbing the simulation.
          if (changed && graphRef && graphRef.current && typeof graphRef.current.refresh === 'function') {
            graphRef.current.refresh();
          }

          setTokenList((prev) => {
            if (!Array.isArray(prev) || !prev.length) return prev;
            let changed = false;
            const next = prev.map((row) => {
              const rawId = row && (row.id || row.address);
              const addr = normalizeAddress(rawId);
              if (!addr) return row;
              const meta = addressMetaCacheRef.current.get(addr);
              if (!meta) return row;
              const nextLabel = meta.name || meta['n.name'] || meta.symbol || meta['n.symbol'] || row.label;
              const nextIcon = meta.icon || meta['n.icon'] || meta.icon2 || meta['n.icon2'] || row.icon || null;
              if (nextLabel && nextLabel !== row.label) {
                changed = true;
                return { ...row, label: nextLabel, icon: nextIcon };
              }
              if (nextIcon && nextIcon !== row.icon) {
                changed = true;
                return { ...row, icon: nextIcon };
              }
              return row;
            });
            return changed ? next : prev;
          });
        }
      } catch (e) {
        if (cancelled) return;
      } finally {
        // Always clear the resolving flag when the run finishes.
        if (!cancelled) {
          setResolvingNames(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [graphData]);

  const linkCount = useMemo(() => {
    if (!graphData || !Array.isArray(graphData.links)) return new Map();

    const map = new Map();
    graphData.links.forEach((link) => {
      const rawSource = typeof link.source === 'string' ? link.source : link.source?.id;
      const rawTarget = typeof link.target === 'string' ? link.target : link.target?.id;
      const source = rawSource ? String(rawSource).toLowerCase() : '';
      const target = rawTarget ? String(rawTarget).toLowerCase() : '';

      if (source) {
        map.set(source, (map.get(source) || 0) + 1);
      }
      if (target) {
        map.set(target, (map.get(target) || 0) + 1);
      }
    });

    return map;
  }, [graphData]);

  const maxLinkValue = useMemo(() => {
    if (!graphData || !Array.isArray(graphData.links) || graphData.links.length === 0) return 1;
    let max = 1;
    graphData.links.forEach((link) => {
      const v = link.value || 1;
      if (v > max) max = v;
    });
    return max;
  }, [graphData]);

  const handleNodeClick = useCallback((dataSource, _graphData, _graphRef, nodeId) => {
    if (!nodeId) return;
    if (onPromotePrimary) {
      onPromotePrimary(nodeId);
    }
  }, [onPromotePrimary]);

  const handleNodeRightClick = useCallback((node, event) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    try {
      console.log('[forensics] node right-click', { id: node && node.id, address: node && node.address });
    } catch (e) {
    }
    setNodeContextMenu({
      mouseX: event && typeof event.clientX === 'number' ? event.clientX : 0,
      mouseY: event && typeof event.clientY === 'number' ? event.clientY : 0,
      node,
    });
  }, []);

  const handleEditNameTableFromContextMenu = useCallback(() => {
    if (!nodeContextMenu || !nodeContextMenu.node) return;
    const node = nodeContextMenu.node;
    setNodeContextMenu(null);
    try {
      try {
        console.log('[forensics] Edit Nametable clicked', { id: node && node.id, address: node && node.address });
      } catch (e) {
      }
      if (!node || !node.id) {
        console.warn('[forensics] nametable: missing node id');
        return;
      }

      if (typeof window !== 'undefined' && typeof window.openNameTableDialogForNode === 'function') {
        console.log('[forensics] calling window.openNameTableDialogForNode');
        window.openNameTableDialogForNode(node);
        return;
      }

      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
        console.log('[forensics] dispatching open-nametable-dialog event');
        window.dispatchEvent(new CustomEvent('open-nametable-dialog', { detail: { node } }));
      } else {
        console.warn('[forensics] nametable: no global handler available');
      }
    } catch (e) {
    }
  }, [nodeContextMenu]);

  const noop = useCallback(() => {}, []);

  if (!token) {
    return <div className={classes.inlineEmpty}>Select a story with a primary token to view evidence.</div>;
  }

  if (loading && !graphData.nodes.length) {
    const showProgress = seedTokens && seedTokens.length > 1 && loadProgress && loadProgress.total > 0;
    const label = showProgress
      ? `Loading sample wallets… ${Math.min(loadProgress.done, loadProgress.total)}/${loadProgress.total}`
      : 'Loading evidence…';
    return (
      <div className={classes.inlineEmpty}>
        <CircularProgress size={16} style={{ marginRight: 8 }} />
        <span>{label}</span>
      </div>
    );
  }

  if (error && !graphData.nodes.length) {
    return <div className={classes.inlineEmpty}>{error}</div>;
  }

  const effectiveShowList = !!showList || !!forceShowList;
  const showGraphOnly = !!showGraph && !effectiveShowList;
  const shouldSideBySide = !!sideBySide && !!showGraph && effectiveShowList && !showGraphOnly;
  const showSplitter = !!showGraph && effectiveShowList && !showGraphOnly && !shouldSideBySide;

  return (
    <div
      className={classes.root}
      ref={rootRef}
      style={shouldSideBySide ? { flexDirection: 'row', alignItems: 'stretch' } : undefined}
    >
      {pumpSignals.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8, padding: '0 2px' }}>
          {pumpSignals.map((chip) => (
            <Chip
              key={chip.label}
              size="small"
              label={chip.label}
              style={{
                height: 20,
                fontSize: 10,
                fontWeight: 800,
                color: chip.color,
                backgroundColor: chip.background,
                border: `1px solid ${chip.color}33`,
              }}
            />
          ))}
        </div>
      )}

      {showGraph && (
        <div
          className={classes.graphContainer}
          ref={graphContainerRef}
          style={shouldSideBySide
            ? { flex: '2 1 0%', minWidth: 0 }
            : (graphFlex != null
              ? { flex: `${graphFlex} 1 0%`, flexBasis: 0 }
              : (showSplitter ? { flex: `${splitRatio} 1 0%`, flexBasis: 0 } : undefined))}
        >
          {showGraphToolbar && (
            <div
              style={{
                position: 'absolute',
                top: 10,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 7,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.85)' }}>Labels</div>
                <Tooltip title={showLabels ? 'Hide labels' : 'Show labels'}>
                  <Switch
                    checked={showLabels}
                    onChange={(e) => setShowLabels(!!e.target.checked)}
                    size="small"
                    color="default"
                  />
                </Tooltip>
                <div style={{ width: 80, paddingTop: 2 }}>
                  <Slider
                    value={labelSize}
                    min={2}
                    max={20}
                    step={1}
                    onChange={(e, v) => setLabelSize(Number(v) || 0)}
                  />
                </div>
              </div>
            </div>
          )}

          {showSlowSpinner && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)', zIndex: 5, pointerEvents: 'none' }}>
              <CircularProgress size={18} />
            </div>
          )}
          {wantsCollapsed && !expanded && (
            <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Button
                variant="contained"
                size="small"
                style={{ background: 'rgba(77,121,255,0.9)', color: '#fff', textTransform: 'none' }}
                onClick={() => setExpanded(true)}
              >
                Expand graph
              </Button>
              <Typography variant="caption" style={{ color: '#cfd3ff' }}>
                Showing wallets only
              </Typography>
            </div>
          )}
          {graphData.nodes.length ? (
            <GraphComponent
              isMobile={false}
              is3DMode={true}
              graphData={graphData}
              graphRef={graphRef}
              handleBackgroundClick={noop}
              handleNodeDragEnd={noop}
              handleNodeDrag={noop}
              handleEngineStop={noop}
              handleZoom={noop}
              handleClick={handleNodeClick}
              handleNodeRightClick={handleNodeRightClick}
              handleNodeDoubleClick={noop}
              dataSource={rabbitMeta ? "RABBIT" : "ETHERSCAN"}
              filterNFT={false}
              maxValue={maxLinkValue}
              imageSize={24}
              stopCameraSpin={noop}
              startCameraSpin={noop}
              setActiveLinkId={noop}
              handleNodeClick={handleNodeClick}
              showText={showLabels}
              showChainLabels={false}
              width={graphWidth || 600}
              height={graphHeight || 280}
              currentView={"/stories"}
              spinOnMount={false}
              labelSize={labelSize}
            />
          ) : (
            (!loading && emptyGraceExpired) ? (
              <div className={classes.inlineEmpty}>No counterparties found in the last day.</div>
            ) : null
          )}

          {nodeContextMenu && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 4000,
              }}
              onClick={() => setNodeContextMenu(null)}
              onContextMenu={(e) => e.preventDefault()}
            >
              <div
                style={{
                  position: 'absolute',
                  top: nodeContextMenu.mouseY,
                  left: nodeContextMenu.mouseX,
                  backgroundColor: '#111',
                  border: '1px solid #444',
                  padding: '4px 0',
                  color: '#fff',
                  fontSize: 12,
                  minWidth: 140,
                  textAlign: 'left',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    padding: '4px 12px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                  }}
                  onClick={handleEditNameTableFromContextMenu}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#222'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  Edit Nametable
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showSplitter && (
        <div
          className={classes.splitter}
          onPointerDown={startSplitDrag}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize evidence panels"
        >
          <div className={classes.splitterBar} />
        </div>
      )}

      {effectiveShowList && (
        <div
          className={classes.listContainer}
          style={shouldSideBySide
            ? { flex: listFlex || 1, marginLeft: 10 }
            : (listFlex != null
              ? { flex: `${listFlex} 1 0%`, flexBasis: 0 }
              : (showSplitter ? { flex: `${1 - splitRatio} 1 0%`, flexBasis: 0 } : undefined))}
        >
          {loading && (
            <div className={classes.inlineEmpty}>
              <CircularProgress size={16} style={{ marginRight: 8 }} />
              <span>Loading evidence…</span>
            </div>
          )}
          {!loading && !tokenList.length && emptyGraceExpired && (
            <div className={classes.inlineEmpty}>No counterparties found in the last day.</div>
          )}
          <NodeList
            key={`${token || ''}-${(graphData && graphData.nodes ? graphData.nodes.length : 0)}-${(graphData && graphData.links ? graphData.links.length : 0)}`}
            graphData={graphData}
            graphRef={graphRef}
            dataSource={rabbitMeta ? "RABBIT" : "ETHERSCAN"}
            linkCount={linkCount}
            getTransactions={noop}
            handleNodeClick={handleNodeClick}
            stopCameraSpin={noop}
            isMobile={false}
            isPortrait={false}
            tokenType={"Token"}
            tokenList={tokenList}
            viewGraph={true}
            rangeSelected={"1W"}
            organicLayout={false}
            tokenView={"stories"}
            showPriceColumns={false}
            showAddressNodes={true}
            useColorDots={true}
          />
        </div>
      )}
    </div>
  );
}

function buildGraphFromRabbitMeta(primaryToken, rabbitMeta, primaryTokenMeta) {
  const entities = rabbitMeta && rabbitMeta.entities ? rabbitMeta.entities : {};
  const chain = (rabbitMeta && rabbitMeta.chain) ? String(rabbitMeta.chain).toUpperCase() : 'ETH';
  const sink = entities.sink || primaryToken;
  const anchors = Array.isArray(entities.anchors) ? entities.anchors : [];
  const group = Array.isArray(entities.group) ? entities.group : [];
  const sharedCounterparties = Array.isArray(entities.shared_counterparties) ? entities.shared_counterparties : [];
  const topSources = Array.isArray(entities.top_sources) ? entities.top_sources : [];
  const topMids = Array.isArray(entities.top_mids) ? entities.top_mids : [];
  const outTo = Array.isArray(entities.out_to) ? entities.out_to : [];

  const explicitNodes = Array.isArray(entities.nodes) ? entities.nodes : [];
  const explicitEdges = Array.isArray(entities.edges) ? entities.edges : [];

  const nodesMap = new Map();
  const links = [];

  const addNode = (id, role) => {
    if (!id || nodesMap.has(id)) return;
    const baseLabel = formatShortAddress(id);
    let label = baseLabel;
    if (role === 'sink' && primaryTokenMeta && (primaryTokenMeta.name || primaryTokenMeta.symbol)) {
      label = primaryTokenMeta.name || primaryTokenMeta.symbol;
    }
    nodesMap.set(id, {
      id,
      label,
      chain,
      color: colorHexToNumber(getColorForAddress(id)),
      expandScale: role === 'sink' ? 1.8 : undefined,
      metaType: role,
    });
  };

  addNode(sink, 'sink');
  anchors.forEach((addr) => addNode(addr, 'anchor'));
  group.forEach((addr) => addNode(addr, 'group'));
  sharedCounterparties.forEach((addr) => addNode(addr, 'shared_counterparty'));
  topSources.forEach((addr) => addNode(addr, 'source'));
  topMids.forEach((addr) => addNode(addr, 'mid'));
  outTo.forEach((addr) => addNode(addr, 'out'));

  if ((explicitNodes && explicitNodes.length) || (explicitEdges && explicitEdges.length)) {
    const anchorId = entities && entities.anchor && entities.anchor.id ? String(entities.anchor.id) : null;
    if (anchorId && !nodesMap.has(anchorId)) {
      addNode(anchorId, 'anchor');
    }

    explicitNodes.forEach((n) => {
      if (!n) return;
      const id = n.id ? String(n.id) : '';
      if (!id) return;
      const role = n.role ? String(n.role) : (n.kind ? String(n.kind) : 'node');
      addNode(id, role);
    });

    explicitEdges.forEach((e, index) => {
      if (!e) return;
      const src = e.from || e.src || e.source;
      const dst = e.to || e.dst || e.target;
      if (!src || !dst) return;
      addNode(String(src), 'edge');
      addNode(String(dst), 'edge');
      links.push({
        id: e.tx_hash ? String(e.tx_hash) : `rabbit-e-${index}`,
        source: String(src),
        target: String(dst),
        value: (e.value_eth != null ? Number(e.value_eth) : (e.value != null ? Number(e.value) : 1)) || 1,
        tx: e.tx_hash ? String(e.tx_hash) : undefined,
      });
    });
  }

  anchors.forEach((addr, index) => {
    if (!addr) return;
    if (String(addr).toLowerCase() === String(sink).toLowerCase()) return;
    links.push({
      id: `anc-${addr}-${index}`,
      source: addr,
      target: sink,
      value: 0.6,
    });
  });

  // Mirror stories: connect each group member to each shared counterparty.
  // This yields a complete, connected representation of the "lockstep" set.
  group.forEach((member) => {
    if (!member) return;
    sharedCounterparties.forEach((cp, index) => {
      if (!cp) return;
      links.push({
        id: `mir-${member}-${cp}-${index}`,
        source: member,
        target: cp,
        value: 0.8,
      });
    });
  });

  topSources.forEach((addr, index) => {
    if (!addr) return;
    links.push({
      id: `src-${addr}-${index}`,
      source: addr,
      target: sink,
      value: 1,
    });
  });

  topMids.forEach((addr, index) => {
    if (!addr) return;
    links.push({
      id: `mid-${addr}-${index}`,
      source: addr,
      target: sink,
      value: 0.7,
    });
  });

  const evidence = rabbitMeta && rabbitMeta.evidence ? rabbitMeta.evidence : {};
  const sampleEdges = Array.isArray(evidence.sample_edges) ? evidence.sample_edges : [];
  sampleEdges.forEach((edge, index) => {
    if (!edge || !edge.src || !edge.dst) return;
    addNode(edge.src, 'edge');
    addNode(edge.dst, 'edge');
    links.push({
      id: `ev-${index}`,
      source: edge.src,
      target: edge.dst,
      value: 0.5,
    });
  });

  let nodes = Array.from(nodesMap.values());
  nodes = nodes.sort((a, b) => {
    if (a.id === sink && b.id !== sink) return -1;
    if (b.id === sink && a.id !== sink) return 1;
    const aNamed = a.label && !String(a.label).startsWith('0x');
    const bNamed = b.label && !String(b.label).startsWith('0x');
    if (aNamed !== bNamed) return bNamed - aNamed;
    return 0;
  });

  const tokenRows = [];
  const seen = new Set();
  nodes.forEach((node) => {
    const id = (node.id || '').toLowerCase();
    if (!id || seen.has(id)) return;
    seen.add(id);
    tokenRows.push({
      address: id,
      label: node.label,
      chain: node.chain,
      type: node.metaType,
    });
  });

  const tokenList = createTokenList(tokenRows, true, sink, primaryTokenMeta);
  return { graphData: { nodes, links }, tokenList };
}

function buildGraphFromFlowMeta(primaryToken, flowMeta, primaryTokenMeta) {
  const chain = (flowMeta && flowMeta.chain) ? String(flowMeta.chain).toUpperCase() : 'ETH';
  const focusWallet = (flowMeta && (flowMeta.wallet || flowMeta.seed_id)) ? (flowMeta.wallet || flowMeta.seed_id) : primaryToken;
  const stats = flowMeta && flowMeta.stats ? flowMeta.stats : {};
  const topCounterparty = stats.top_counterparty || null;

  const focusMeta = {
    chain,
    icon: flowMeta && flowMeta.wallet_icon ? flowMeta.wallet_icon : (primaryTokenMeta && primaryTokenMeta.icon ? primaryTokenMeta.icon : undefined),
    name: flowMeta && flowMeta.wallet_name ? flowMeta.wallet_name : (primaryTokenMeta && primaryTokenMeta.name ? primaryTokenMeta.name : undefined),
    symbol: flowMeta && flowMeta.wallet_symbol ? flowMeta.wallet_symbol : (primaryTokenMeta && primaryTokenMeta.symbol ? primaryTokenMeta.symbol : undefined),
  };

  const rawNodes = Array.isArray(flowMeta && flowMeta.nodes) ? flowMeta.nodes : [];
  const rawEdges = Array.isArray(flowMeta && flowMeta.edges) ? flowMeta.edges : [];

  const nodesMap = new Map();
  const links = [];

  const addNode = (id, kind) => {
    if (!id || nodesMap.has(id)) return;
    const isFocus = String(id).toLowerCase() === String(focusWallet).toLowerCase();
    const label = isFocus
      ? (focusMeta.name || focusMeta.symbol || formatShortAddress(id))
      : formatShortAddress(id);
    nodesMap.set(id, {
      id,
      label,
      chain,
      icon: isFocus ? focusMeta.icon : undefined,
      color: colorHexToNumber(getColorForAddress(id)),
      expandScale: isFocus ? 1.8 : undefined,
      kind,
      metaType: kind,
    });
  };

  // Always include the focus wallet.
  addNode(focusWallet, 'wallet_focus');
  if (topCounterparty) addNode(topCounterparty, 'top_counterparty');

  rawNodes.forEach((n) => {
    if (!n || !n.id) return;
    addNode(n.id, n.kind);
  });

  // Include explicit edges if present ("B" mode).
  rawEdges.forEach((e, index) => {
    if (!e) return;
    const src = e.src || e.source;
    const dst = e.dst || e.target;
    if (!src || !dst) return;
    addNode(src, 'edge');
    addNode(dst, 'edge');
    links.push({
      id: `flow-e-${index}`,
      source: src,
      target: dst,
      value: e.value || e.weight || 1,
    });
  });

  // If no edges were provided, or if some nodes are isolated, connect them
  // back to the focus wallet to keep the graph connected and readable.
  const degree = new Map();
  const bump = (id) => degree.set(id, (degree.get(id) || 0) + 1);
  links.forEach((l) => {
    const s = typeof l.source === 'object' ? l.source.id : l.source;
    const t = typeof l.target === 'object' ? l.target.id : l.target;
    if (s) bump(s);
    if (t) bump(t);
  });

  const ensureConnected = (id, index, weight = 0.3) => {
    if (!id) return;
    if (String(id).toLowerCase() === String(focusWallet).toLowerCase()) return;
    if ((degree.get(id) || 0) > 0) return;
    links.push({
      id: `flow-star-${id}-${index}`,
      source: id,
      target: focusWallet,
      value: weight,
    });
    bump(id);
    bump(focusWallet);
  };

  // Ensure top counterparty is connected and visually emphasized.
  if (topCounterparty) {
    ensureConnected(topCounterparty, 'top', 1.2);
  }

  const allIds = Array.from(nodesMap.keys());
  allIds.forEach((id, idx) => ensureConnected(id, idx));

  // Cap node count for performance, while always keeping focus + top cp.
  const MAX_NODES = 450;
  const keep = new Set([focusWallet]);
  if (topCounterparty) keep.add(topCounterparty);

  // Rank remaining nodes by degree (descending) so we keep the most connected.
  const ranked = allIds
    .filter((id) => !keep.has(id))
    .sort((a, b) => (degree.get(b) || 0) - (degree.get(a) || 0));

  ranked.slice(0, Math.max(0, MAX_NODES - keep.size)).forEach((id) => keep.add(id));

  const nodes = Array.from(nodesMap.values()).filter((n) => keep.has(n.id));
  const filteredLinks = links.filter((l) => {
    const s = typeof l.source === 'object' ? l.source.id : l.source;
    const t = typeof l.target === 'object' ? l.target.id : l.target;
    return keep.has(s) && keep.has(t);
  });

  // Build token list rows from the graph nodes.
  const tokenRows = [];
  const seen = new Set();
  nodes.forEach((node) => {
    const id = (node.id || '').toLowerCase();
    if (!id || seen.has(id)) return;
    seen.add(id);
    tokenRows.push({
      address: id,
      label: node.label,
      chain: node.chain,
      icon: node.icon,
      type: node.metaType || node.kind,
    });
  });

  const tokenList = createTokenList(tokenRows, true, focusWallet, focusMeta);
  return { graphData: { nodes, links: filteredLinks }, tokenList };
}

function buildGraphFromCounterparties(token, raw, primaryTokenMeta) {
  const nodesMap = new Map();
  const links = [];

  // Ensure center token node exists
  nodesMap.set(token, {
    id: token,
    label: (primaryTokenMeta && (primaryTokenMeta.name || primaryTokenMeta.symbol))
      ? (primaryTokenMeta.name || primaryTokenMeta.symbol)
      : formatShortAddress(token),
    icon: primaryTokenMeta && primaryTokenMeta.icon ? primaryTokenMeta.icon : undefined,
    chain: primaryTokenMeta && primaryTokenMeta.chain
      ? String(primaryTokenMeta.chain).toUpperCase()
      : 'ETH',
    color: colorHexToNumber(getColorForAddress(token)),
    // Make the primary node visually larger than counterparties in the
    // compact inline stories graph.
    expandScale: 1.8,
  });

  raw.forEach((row, index) => {
    const counterparty = row.counterparty || row.address || row.to || row.from;
    if (!counterparty) return;

    if (!nodesMap.has(counterparty)) {
      const name = row.name || row.label || row.symbol || null;
      const displayLabel = name || formatShortAddress(counterparty);
      const chain = (row.chain || '').toUpperCase() || 'ETH';
      nodesMap.set(counterparty, {
        id: counterparty,
        label: displayLabel,
        icon: row.icon || null,
        chain,
        color: colorHexToNumber(getColorForAddress(counterparty)),
      });
    }

    links.push({
      id: `${token}-${counterparty}-${index}`,
      source: token,
      target: counterparty,
      value: row.value || 1,
    });
  });

  let nodes = Array.from(nodesMap.values());

  // Sort nodes so the primary token is first, then any nodes with a
  // human-friendly name or icon, followed by plain address-only nodes.
  nodes = nodes.sort((a, b) => {
    if (a.id === token && b.id !== token) return -1;
    if (b.id === token && a.id !== token) return 1;

    const aHasNameOrIcon = !!a.icon || (a.label && !String(a.label).startsWith('0x'));
    const bHasNameOrIcon = !!b.icon || (b.label && !String(b.label).startsWith('0x'));

    if (aHasNameOrIcon !== bHasNameOrIcon) {
      return bHasNameOrIcon - aHasNameOrIcon;
    }

    return 0;
  });
  return { nodes, links };
}

function buildGraphFromMultiCounterparties(seedTokens, rawBySeed, primaryTokenMeta) {
  const nodesMap = new Map();
  const links = [];

  const addNode = (id, meta, expandScale) => {
    if (!id) return;
    if (nodesMap.has(id)) return;
    nodesMap.set(id, {
      id,
      label: (meta && (meta.name || meta.symbol))
        ? (meta.name || meta.symbol)
        : formatShortAddress(id),
      icon: meta && meta.icon ? meta.icon : undefined,
      chain: meta && meta.chain ? String(meta.chain).toUpperCase() : 'ETH',
      color: colorHexToNumber(getColorForAddress(id)),
      expandScale,
    });
  };

  (seedTokens || []).forEach((seed) => {
    addNode(seed, primaryTokenMeta, 1.4);
  });

  (seedTokens || []).forEach((seed) => {
    const raw = rawBySeed && rawBySeed.get ? rawBySeed.get(seed) : null;
    (raw || []).forEach((row, index) => {
      const counterparty = row.counterparty || row.address || row.to || row.from;
      if (!counterparty) return;

      if (!nodesMap.has(counterparty)) {
        const name = row.name || row.label || row.symbol || null;
        const displayLabel = name || formatShortAddress(counterparty);
        const chain = (row.chain || '').toUpperCase() || 'ETH';
        nodesMap.set(counterparty, {
          id: counterparty,
          label: displayLabel,
          icon: row.icon || null,
          chain,
          color: colorHexToNumber(getColorForAddress(counterparty)),
        });
      }

      links.push({
        id: `${seed}-${counterparty}-${index}`,
        source: seed,
        target: counterparty,
        value: row.value || 1,
      });
    });
  });

  const nodes = Array.from(nodesMap.values());
  return { nodes, links };
}

function formatShortAddress(address) {
  if (address && address.length > 10 && address.startsWith('0x')) {
    const start = address.substring(0, 6);
    const end = address.substring(address.length - 4);
    return `${start}…${end}`;
  }
  return address;
}

function getColorForAddress(address) {
  // Deterministic pastel-ish color based on address hash so the same address
  // gets the same color in the graph and list. Return a #rrggbb hex so the
  // existing numeric conversion logic can preserve distinct colors.
  if (!address) return '#4d79ff';
  let hash = 0;
  for (let i = 0; i < address.length; i += 1) {
    hash = ((hash << 5) - hash) + address.charCodeAt(i);
    hash |= 0; // Convert to 32bit int
  }
  const r = (Math.abs(hash) & 0xFF0000) >> 16;
  const g = (Math.abs(hash) & 0x00FF00) >> 8;
  const b = Math.abs(hash) & 0x0000FF;
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function colorHexToNumber(color) {
  // Accept hsl(...) or #rrggbb; for hsl we just fall back to a default.
  if (!color) return 0x4d79ff;
  if (color.startsWith('#')) {
    return parseInt(color.slice(1), 16) || 0x4d79ff;
  }
  // ForceGraph accepts CSS color strings too, but other callers use numeric,
  // so keep a stable fallback.
  return 0x4d79ff;
}

function createTokenList(rows, addAllAddresses = false, primaryToken, primaryTokenMeta) {
  if (!Array.isArray(rows) || !rows.length) return [];

  const list = [];
  const seen = new Set();

  rows.forEach((row) => {
    const address = (row.counterparty || row.address || row.to || row.from || '').toLowerCase();
    if (!address || seen.has(address)) return;

    const name = row.name || row.label || row.symbol || '';

    // If addAllAddresses is true, we include everything; otherwise mimic a
    // light "token-like" filter similar to NodeList.
    if (!addAllAddresses) {
      const looksLikeToken =
        (row.nodeType === 'token') ||
        !!row.icon ||
        (typeof name === 'string' && !name.startsWith('0x'));
      if (!looksLikeToken) return;
    }

    seen.add(address);

    const icon = row.icon || null;
    const color = icon ? null : getColorForAddress(address);

    list.push({
      id: address,
      // Prefer a human-friendly token name; fall back to symbol, then address.
      label: name || row.symbol || formatShortAddress(address),
      symbol: row.symbol || '',
      icon,
      color,
      likes: row.likes || 0,
      chain: (row.chain || '').toUpperCase(),
      count: row.count || row.txCount || 0,
      type: row.type || row.metaType || row.kind || '',
    });
  });

  // Ensure the primary token itself appears in the list with its best
  // available metadata so the NodeList always includes the center node,
  // even though the counterparties query only returns neighbors.
  if (primaryToken && !seen.has(primaryToken.toLowerCase())) {
    const addr = primaryToken.toLowerCase();
    const label = (primaryTokenMeta && (primaryTokenMeta.name || primaryTokenMeta.symbol))
      ? (primaryTokenMeta.name || primaryTokenMeta.symbol)
      : formatShortAddress(primaryToken);
    const icon = primaryTokenMeta && primaryTokenMeta.icon ? primaryTokenMeta.icon : null;
    const chain = primaryTokenMeta && primaryTokenMeta.chain
      ? String(primaryTokenMeta.chain).toUpperCase()
      : '';

    list.unshift({
      id: addr,
      label,
      symbol: primaryTokenMeta && primaryTokenMeta.symbol ? primaryTokenMeta.symbol : '',
      icon,
      color: icon ? null : getColorForAddress(addr),
      likes: primaryTokenMeta && typeof primaryTokenMeta.likes === 'number' ? primaryTokenMeta.likes : 0,
      chain,
      count: 0,
      type: 'primary',
    });
  }

  return list;
}
